import { useEffect, useState } from 'react'
import { Gift } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { referralsService, type PartnerPromotion, type PortalReferral } from '@/services/referralsService'
import { PARCEIROS_ACCENT } from './parceirosBrand'
import {
  DEFAULT_PARTNER_PROMOTION,
  buildPremioTicks,
  buildPremioProgress,
  formatPremioBrl,
  isPromotionCurrent,
  mesPremioLabel,
} from './parceirosPremio'

type Props = {
  rows: PortalReferral[]
  loading: boolean
}

export function ParceirosPremioCard({ rows, loading }: Props) {
  const [promotions, setPromotions] = useState<PartnerPromotion[]>([DEFAULT_PARTNER_PROMOTION])
  const [imageOk, setImageOk] = useState(true)
  const [imageOpen, setImageOpen] = useState(false)
  const promotion =
    promotions.find(isPromotionCurrent) ??
    promotions.find((item) => item.isActive) ??
    promotions[0] ??
    DEFAULT_PARTNER_PROMOTION
  const premio = buildPremioProgress(rows, promotion)
  const ticks = buildPremioTicks(premio.meta)
  const imageUrl = promotion.imageUrl || DEFAULT_PARTNER_PROMOTION.imageUrl
  const showImage = Boolean(imageUrl && imageOk)

  useEffect(() => {
    let cancelled = false
    referralsService
      .listMyPromotions()
      .then((items) => {
        if (!cancelled && items.length > 0) {
          setPromotions(items)
          setImageOk(true)
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <section className='overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-[var(--bg-card)]'>
      <div className='grid gap-0 lg:grid-cols-[minmax(280px,420px)_1fr]'>
        <div className='relative min-h-[280px] bg-white'>
          {showImage ? (
            <button
              type='button'
              onClick={() => setImageOpen(true)}
              className='block h-full w-full cursor-zoom-in'
              aria-label={`Ampliar imagem: ${promotion.rewardName}`}
            >
              <img
                src={imageUrl ?? ''}
                alt={promotion.rewardName}
                className='h-full w-full object-contain'
                onError={() => setImageOk(false)}
              />
            </button>
          ) : (
            <div className='flex h-full min-h-[280px] flex-col items-center justify-center gap-3 border-b border-dashed border-[var(--glass-border)] px-6 text-center lg:border-b-0 lg:border-r'>
              <span className='flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]'>
                <Gift size={26} />
              </span>
              <p className='text-sm font-medium text-text-primary'>Imagem da premiação</p>
              <p className='text-xs text-text-muted'>Espaço para a foto do prêmio do mês.</p>
            </div>
          )}
        </div>

        <div className='grid gap-6 p-5 md:p-6'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-wide text-text-muted'>
              {promotion.title} · {mesPremioLabel(promotion)}
            </p>
            <h2 className='mt-1 text-2xl font-bold tracking-tight text-text-primary'>{promotion.rewardName}</h2>
            <p className='mt-2 max-w-2xl text-sm text-text-muted'>
              {promotion.bannerText || promotion.description}
            </p>
          </div>

          <div>
            <div className='mb-2 flex items-end justify-between gap-3'>
              <p className='text-sm font-medium text-text-primary'>
                {loading
                  ? 'Carregando meta…'
                  : `${formatPremioBrl(premio.valorFechado)} de ${formatPremioBrl(premio.meta)}`}
              </p>
              <p className='text-sm text-text-muted'>
                {premio.bateu ? 'Meta batida' : `Faltam ${formatPremioBrl(premio.faltam)}`}
              </p>
            </div>
            <div className='relative h-3 rounded-full bg-white/10'>
              <div
                className='absolute inset-y-0 left-0 rounded-full transition-[width]'
                style={{ width: `${premio.pct}%`, background: PARCEIROS_ACCENT }}
              />
            </div>
            <div className='mt-2 flex justify-between text-[11px] text-text-muted'>
              {ticks.map((tick) => (
                <span
                  key={tick}
                  className={tick <= premio.valorFechado ? 'font-semibold text-text-primary' : undefined}
                >
                  {formatPremioBrl(tick)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className='border-t border-[var(--glass-border)] p-5 md:p-6'>
        <h3 className='text-sm font-semibold text-text-primary'>Histórico de promoções</h3>
        <div className='mt-3 grid gap-2'>
          {promotions.map((item) => {
            const progress = buildPremioProgress(rows, item)
            return (
              <article key={item.id} className='rounded-xl bg-white/[0.03] px-3 py-2'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div>
                    <p className='text-sm font-medium text-text-primary'>{item.rewardName}</p>
                    <p className='text-xs text-text-muted'>
                      {item.title} · {mesPremioLabel(item)}
                    </p>
                  </div>
                  <span className='text-sm font-semibold text-text-primary'>
                    {formatPremioBrl(progress.valorFechado)} / {formatPremioBrl(progress.meta)}
                  </span>
                </div>
                <div className='mt-2 h-2 rounded-full bg-white/10'>
                  <div
                    className='h-2 rounded-full'
                    style={{ width: `${progress.pct}%`, background: PARCEIROS_ACCENT }}
                  />
                </div>
              </article>
            )
          })}
        </div>
      </div>

      <Modal
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        title={promotion.rewardName}
        size='2xl'
      >
        <img
          src={imageUrl ?? ''}
          alt={promotion.rewardName}
          className='mx-auto max-h-[70vh] w-full rounded-lg bg-white object-contain'
        />
      </Modal>
    </section>
  )
}
